import { faHistory, faMinus, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons'
import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { Button } from '@/components'
import { useAppContext } from '@/hooks'
import { SelectWithoutForm } from '@/lib/components/Select/EnhancedSyncSelect/EnhancedSyncSelect'
import { useSelectedBranchNumericId } from '@/pages/User/pages/Home/components/TreePanel/TreePanelContext'
import { useGetStructureHistoryQuery } from '@/rtkApi'

import { useDetailTabContext } from '../context/DetailTabContext'
import { useReplaceVersionData } from '../hooks/useReplaceVersionData'
import { RestoreVersionDialog } from './RestoreVersionDialog'
import { SelectEnv } from './SelectEnv'

type ViewHistoryProps = {
	onChangeActive: (active: boolean) => void
	onChangeVersion: (version?: number) => void
	onChangeVersionMax: (versionMax?: number) => void
}

export const ViewHistory = ({
	onChangeVersion,
	onChangeActive,
	onChangeVersionMax,
}: ViewHistoryProps) => {
	const [version, setVersion] = useState(1)
	const [active, setActive] = useState(false)
	const { t } = useAppContext()

	const {
		state: { node },
	} = useDetailTabContext()
	const branchId = useSelectedBranchNumericId()

	const changeVersion = useCallback(
		(version: number) => setVersion(version),
		[],
	)

	const historyList = useGetStructureHistoryQuery(
		{
			branchId: branchId ?? 0,
			structureId: node.id,
			pageNumber: 1,
			pageSize: 200,
		},
		{ skip: branchId == null || !node.id },
	)
	const historyItems = historyList.data?.content

	const { replaceVersionData, replacing } = useReplaceVersionData(changeVersion)

	const handleRestore = useCallback(async () => {
		await replaceVersionData()
		await historyList.refetch()
	}, [replaceVersionData, historyList])

	const versions = useMemo(
		() => historyItems?.map((history) => history.version) ?? [1],
		[historyItems],
	)

	const currentVersionDeployments = useMemo(() => {
		const historyEntry = historyItems?.find((h) => h.version === version)
		return historyEntry?.deployments
	}, [historyItems, version])

	const { versionMin, versionMax } = useMemo(() => {
		const versionMin = Math.min(...versions)
		const versionMax = Math.max(...versions)

		return {
			versionMin,
			versionMax,
		}
	}, [versions])

	useEffect(() => {
		onChangeVersionMax(versionMax)
	}, [onChangeVersionMax, versionMax])

	useEffect(() => {
		onChangeActive(active)
	}, [onChangeActive, active])

	useEffect(() => {
		setVersion(versionMax)
	}, [versionMax])

	useEffect(() => {
		onChangeVersion(active ? version : undefined)
	}, [active, onChangeVersion, version])

	return (
		<Wrapper>
			{!active && (
				<Button
					onClick={() => setActive(true)}
					icon={faHistory}
					schema="transparent"
					size="sm"
					disabled={historyList.isLoading || !historyList.data?.length}
				>
					{t('VIEW_HISTORY')}
				</Button>
			)}
			{active && (
				<>
					<SelectEnv replacing={replacing} deployments={currentVersionDeployments} />
					{version < versionMax && (
						<RestoreVersionDialog
							onRestore={handleRestore}
							replacing={replacing}
						/>
					)}
					<Button
						onClick={() => setVersion((prev) => prev - 1)}
						icon={faMinus}
						schema="transparent"
						size="sm"
						disabled={versionMin >= version || replacing}
					/>
					<VersionSelectWrapper>
					<SelectWithoutForm
						name="version"
						value={version}
						options={historyList.data}
						loading={historyList.isLoading}
						valueKey="version"
						labelKey="version"
							clearable={false}
						isNumeric
							onChange={(value: number | undefined) => {
							if (value === undefined) {
								setActive(false)
								setVersion(versionMax)

								return
							}

							setVersion(value)
						}}
						disabled={replacing}
							testId="history-version"
					/>
					</VersionSelectWrapper>
					<Button
						onClick={() => setVersion((prev) => prev + 1)}
						icon={faPlus}
						schema="transparent"
						size="sm"
						disabled={versionMax <= version || replacing}
					/>
					<Button
						onClick={() => {
							setActive(false)
							setVersion(versionMax)
						}}
						icon={faTimes}
						schema="transparent"
						size="sm"
						disabled={replacing}
					/>
				</>
			)}
		</Wrapper>
	)
}

const Wrapper = styled.div`
	margin-left: auto;
	display: flex;
	align-items: center;
	justify-content: flex-end;
	padding-left: 6px;
`

const VersionSelectWrapper = styled.div`
	width: 5rem;
	flex-shrink: 0;
`
